import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { norm } from "@/lib/normalize";
import { formatINR } from "@/lib/currency";

const emptyItem = { productId: "", productName: "", quantity: 1, unit: "", unitPrice: 0, gstPercentage: 0, discount: 0 };

const emptyForm = {
  supplierId: "",
  purchaseDate: "",
  invoiceNumber: "",
  invoiceDate: "",
  paidAmount: 0,
  paymentMethod: "",
  paymentStatus: "",
  discountAmount: 0,
  items: [{ ...emptyItem }],
};

function calcLineTotal(item) {
  const subtotal = Number(item.quantity) * Number(item.unitPrice);
  const gst = subtotal * (Number(item.gstPercentage) / 100);
  const discount = Number(item.discount) || 0;
  return Math.max(0, subtotal + gst - discount);
}

export default function Purchases() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const isAdmin = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist";

  const load = async () => {
    try { setRows((await api.get("/purchases")).data); } catch { toast.error("Failed to load purchases"); }
  };

  const loadProducts = async () => {
    try { setProducts((await api.get("/products")).data); } catch { /* ignore */ }
  };

  const loadSuppliers = async () => {
    try { setSuppliers((await api.get("/suppliers")).data); } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadProducts(); loadSuppliers(); }, []);

  const setItem = (idx, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));

  const removeItem = (idx) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const onProductSelect = (idx, productId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    if (product) {
      setItem(idx, {
        productId: String(product.id),
        productName: product.name,
        unit: product.unit || "",
        unitPrice: product.unitPrice || product.costPrice || 0,
        gstPercentage: product.gstPercentage || 0,
      });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        paidAmount: form.paidAmount !== "" && form.paidAmount !== null && form.paidAmount !== undefined ? Number(form.paidAmount) : grandTotal,
        discountAmount: Number(form.discountAmount || 0),
        paymentMethod: form.paymentStatus === "UNPAID" ? null : form.paymentMethod || undefined,
        paymentStatus: form.paymentStatus || undefined,
        items: form.items.map((it) => ({
          productId: Number(it.productId),
          quantity: Number(it.quantity),
          unit: it.unit,
          unitPrice: Number(it.unitPrice),
          gstPercentage: Number(it.gstPercentage),
          discount: Number(it.discount) || 0,
          lineTotal: calcLineTotal(it),
        })),
      };
      if (editId) await api.put(`/purchases/${editId}`, payload);
      else await api.post("/purchases", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete purchase?")) return;
    try { await api.delete(`/purchases/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const grandTotal = form.items.reduce((sum, it) => sum + calcLineTotal(it), 0);

  return (
    <div className="space-y-8" data-testid="purchases-page">
      <div className="flex items-end justify-between gap-4">
        <div><div className="overline mb-2">Procurement</div><h1 className="font-heading text-4xl text-white">Purchases</h1></div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm(emptyForm); setEditId(null); }} data-testid="add-purchase-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Purchase</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} Purchase</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <Label>Supplier *</Label>
                  <Select value={form.supplierId} onValueChange={(val) => setForm({ ...form, supplierId: val })}>
                    <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <div><Label>Purchase Date *</Label><Input required type="date" value={form.purchaseDate} onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1 text-white" /></div>
                  <div><Label>Invoice Number *</Label><Input required value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>Invoice Date</Label><Input type="date" value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1 text-white" /></div>
                </div>

                {/* Purchase Items Grid */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Purchase Items</Label>
                    <button type="button" onClick={addItem} className="text-xs text-gold hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add Item</button>
                  </div>
                  <div className="border border-gold-subtle rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-gold-subtle hover:bg-transparent">
                          <TableHead className="text-emerald-muted text-xs">Product</TableHead>
                          <TableHead className="text-emerald-muted text-xs w-20">Qty</TableHead>
                          <TableHead className="text-emerald-muted text-xs w-24">Unit Price</TableHead>
                          <TableHead className="text-emerald-muted text-xs w-20">GST %</TableHead>
                          <TableHead className="text-emerald-muted text-xs w-24">Discount</TableHead>
                          <TableHead className="text-emerald-muted text-xs w-24 text-right">Line Total</TableHead>
                          <TableHead className="text-emerald-muted w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {form.items.map((item, idx) => (
                          <TableRow key={idx} className="border-gold-subtle">
                            <TableCell className="p-1">
                              <Select value={item.productId} onValueChange={(val) => onProductSelect(idx, val)}>
                                <SelectTrigger className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs">
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map((p) => (
                                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" min="1" value={item.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs" />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" step="0.01" value={item.unitPrice} onChange={(e) => setItem(idx, { unitPrice: e.target.value })} className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs" />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" step="0.01" value={item.gstPercentage} onChange={(e) => setItem(idx, { gstPercentage: e.target.value })} className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs" />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" step="0.01" value={item.discount} onChange={(e) => setItem(idx, { discount: e.target.value })} className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs" />
                            </TableCell>
                            <TableCell className="p-1 text-right text-gold text-xs font-medium">
                              {formatINR(calcLineTotal(item))}
                            </TableCell>
                            <TableCell className="p-1">
                              {form.items.length > 1 && (
                                <button type="button" onClick={() => removeItem(idx)} className="text-emerald-muted hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="text-right mt-2">
                    <span className="text-emerald-muted text-sm">Grand Total: </span><span className="text-gold text-lg font-medium">{formatINR(grandTotal)}</span>
                  </div>
                </div>

                {/* Payment Section */}
                <div>
                  <Label className="text-sm font-medium">Payment</Label>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                    <div><Label>Paid Amount (₹)</Label><Input type="number" step="0.01" value={form.paidAmount ?? grandTotal.toFixed(2)} onChange={(e) => setForm({ ...form, paidAmount: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                    <div><Label>Credit/Discount (₹)</Label><Input type="number" step="0.01" value={form.discountAmount} onChange={(e) => setForm({ ...form, discountAmount: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select value={form.paymentMethod} onValueChange={(val) => setForm({ ...form, paymentMethod: val })}>
                        <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UPI">UPI</SelectItem>
                          <SelectItem value="NetBanking">NetBanking</SelectItem>
                          <SelectItem value="Cheque">Cheque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Payment Status</Label>
                      <Select value={form.paymentStatus} onValueChange={(val) => setForm({ ...form, paymentStatus: val, paidAmount: val === "UNPAID" ? 0 : (val === "PAID" ? (grandTotal - Number(form.discountAmount || 0)) : form.paidAmount) })}>
                        <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PAID">Paid</SelectItem>
                          <SelectItem value="UNPAID">Unpaid</SelectItem>
                          <SelectItem value="PARTIALLY_PAID">Partially Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <button type="submit" className="gold-button w-full h-11 rounded-md">Save Purchase</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              <TableHead className="text-emerald-muted">Supplier</TableHead>
              <TableHead className="text-emerald-muted">Purchase Date</TableHead>
              <TableHead className="text-emerald-muted">Invoice No.</TableHead>
              <TableHead className="text-emerald-muted">Invoice Date</TableHead>
              <TableHead className="text-emerald-muted">Payment Status</TableHead>
              <TableHead className="text-emerald-muted text-right">Total</TableHead>
              {isAdmin && <TableHead className="text-emerald-muted w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} className="border-gold-subtle" data-testid={`purchase-row-${p.id}`}>
                <TableCell className="text-white">{p.supplierName || suppliers.find((s) => String(s.id) === String(p.supplierId))?.name || ""}</TableCell>
                <TableCell className="text-emerald-muted">{p.purchaseDate}</TableCell>
                <TableCell className="text-emerald-muted">{p.invoiceNumber}</TableCell>
                <TableCell className="text-emerald-muted">{p.invoiceDate}</TableCell>
                <TableCell className="text-emerald-muted">{p.paymentStatus || "—"}</TableCell>
                <TableCell className="text-right text-gold">{formatINR(p.totalAmount || 0)}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <button onClick={() => {
                      setForm({
                        supplierId: String(p.supplierId || ""),
                        purchaseDate: p.purchaseDate || "",
                        invoiceNumber: p.invoiceNumber || "",
                        invoiceDate: p.invoiceDate || "",
                        paidAmount: p.paidAmount || 0,
                        paymentMethod: p.paymentMethod || "",
                        paymentStatus: p.paymentStatus || "",
                        discountAmount: p.discountAmount || 0,
                        items: (p.items || []).map((it) => ({
                          productId: String(it.productId || ""),
                          productName: it.productName || "",
                          quantity: it.quantity || 1,
                          unitPrice: it.unitPrice || it.costPrice || 0,
                          gstPercentage: it.gstPercentage || 0,
                        })),
                      });
                      setEditId(p.id); setOpen(true);
                    }} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(p.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-purchase-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
